
import AvailabilityAlertForm from '@/components/AvailabilityAlertForm.vue'
import ProductStockStatus from '@/components/ProductStockStatus.vue'
import VariantsListCarousel from '@/components/VariantsListCarousel.vue'
import AlertSellInSingleCopy from '@/components/AlertSellInSingleCopy.vue'
import favoriteMixin from '@/mixins/favoriteMixin'
import cartMixin from '@/mixins/cartMixin'
import commercialOperationPrice from '@/mixins/commercialOperationPrice'
import userCountryMixin from '@/mixins/userCountryMixin'
import { resolveProductCartContext } from '@/utils/productCartContextStorage'
import {
  freeShippingTitleKey,
  reachesFreeShipping,
} from '@/utils/freeShippingOffer'

export default {
  name: 'ProductConfig',
  components: {
    ProductStockStatus,
    AvailabilityAlertForm,
    VariantsListCarousel,
    AlertSellInSingleCopy,
  },
  mixins: [
    favoriteMixin,
    cartMixin,
    userCountryMixin,
    commercialOperationPrice,
  ],
  props: {
    product: Object,
    name: Boolean,
  },
  data() {
    return {
      option: 1,
      quantity: 1,
      optionsSelected: [],
      selectedBulk: null,
      addingProductToCart: false,
      variantsCompatibles: [],
      isContactAvailable: false,
      showFixedBar: false,
      lastScrollY: 0,
      scrollThreshold: 300,
      scrollUpStart: null,
      scrollUpThreshold: 80,
      scrollTicking: false,
      storedCartContext: null,
    }
  },
  computed: {
    operationPrice() {
      return this.operationPriceOf(this.product)
    },
    priceTtcShown() {
      return (
        this.operationPriceOf(this.product, { beforeTax: false })?.withCode ??
        this.product.variant.user_price_ttc
      )
    },
    isEndOfSeries() {
      return Number(this.product.variant.out_of_stock_status_id) === 4
    },
    showFreeReturns() {
      return (
        this.country === 'FR' &&
        !this.isEndOfSeries &&
        this.product.variant.price_ttc > 20
      )
    },
    estimatedDeliveryDate() {
      const variant = this.product.variant
      const date =
        this.product.fastest_shipping_method_range?.estimated_delivery_date

      if (
        this.country !== 'FR' ||
        !date ||
        variant.quantity_available <= 0 ||
        this.quantity > variant.quantity_available
      ) {
        return null
      }

      return date
    },
    cartMetadata() {
      const { ht, vid, hvd } = this.$route.query
      if (ht && Number(ht)) {
        const meta = {
          headlight_type_id: Number(ht),
          vehicle_id: vid ? Number(vid) || null : null,
        }
        if (hvd && Number(hvd) && Number(hvd) !== -1) {
          meta.headlight_vehicle_detail_id = Number(hvd)
        }
        return meta
      }
      return this.storedCartContext
    },
    contextVehicleId() {
      return this.$store.getters['breadcrumb/vehicle']?.id ?? null
    },
    selectedVehicleId() {
      return this.$store.state.selected_vehicle?.id ?? null
    },
    highestPassiveDiscountRate() {
      return this.product.categories.reduce((max, category) => {
        return Math.max(max, category.passive_discount_rate)
      }, 0)
    },
    freeShippingOffer() {
      return this.$store.getters.free_shipping_offer
    },
    freeShippingReached() {
      return reachesFreeShipping(
        this.freeShippingOffer,
        this.product.variant.user_price_ttc * this.quantity
      )
    },
    freeShippingTitleKey() {
      return freeShippingTitleKey(
        'components.productConfig',
        this.country,
        this.freeShippingOffer
      )
    },
    formattedFreeShipping() {
      return this.$options.filters.priceFormatCompact(
        this.freeShippingOffer?.amount
      )
    },
  },
  watch: {
    'product.id'() {
      this.loadStoredCartContext()
    },
    contextVehicleId() {
      this.loadStoredCartContext()
    },
    selectedVehicleId() {
      this.loadStoredCartContext()
    },
    'product.variant.id'() {
      this.loadCompatibleVariants()
    },
    quantity(val) {
      if (this.product.bulk_discounts) {
        this.product.bulk_discounts.forEach(item => {
          if (item.minimal_quantity < val) {
            this.selectedBulk = item.id
          }
        })
      }
    },
  },

  mounted() {
    this.isContactAvailable = this.computeContactAvailability()
    this.$nextTick(this.loadStoredCartContext)

    if (this.$device.isMobile) {
      window.addEventListener('scroll', this.handleScroll, { passive: true })
    }

    this.loadCompatibleVariants()
  },

  beforeDestroy() {
    if (this.$device.isMobile) {
      window.removeEventListener('scroll', this.handleScroll)
    }
  },

  methods: {
    loadStoredCartContext() {
      this.storedCartContext = resolveProductCartContext(this.product.id, {
        contextVehicleId: this.contextVehicleId,
        selectedVehicleId: this.selectedVehicleId,
      })
    },
    loadCompatibleVariants() {
      this.$productService
        .fetchProductDetailCompatibleVariants(this.product.slug, {
          variant_id: this.product.variant?.id,
        })
        .then(({ data }) => {
          this.variantsCompatibles = data
        })
        .catch(() => {
          this.variantsCompatibles = []
        })
    },
    computeContactAvailability() {
      const parisNow = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' })
      )
      const day = parisNow.getDay()
      const hour = parisNow.getHours()
      const afternoon = hour >= 14 && hour < 18
      if (day === 1) return afternoon
      if (day >= 2 && day <= 5) return (hour >= 10 && hour < 12) || afternoon
      return false
    },
    handleScroll() {
      if (this.scrollTicking) return
      this.scrollTicking = true
      requestAnimationFrame(() => {
        this.updateScrollState()
        this.scrollTicking = false
      })
    },
    updateScrollState() {
      const currentScrollY = window.scrollY

      if (currentScrollY > this.scrollThreshold) {
        if (currentScrollY < this.lastScrollY) {
          if (this.scrollUpStart === null) {
            this.scrollUpStart = this.lastScrollY
          }
          if (this.scrollUpStart - currentScrollY >= this.scrollUpThreshold) {
            this.showFixedBar = false
          }
        } else {
          this.scrollUpStart = null
          this.showFixedBar = true
        }
      } else {
        this.showFixedBar = false
        this.scrollUpStart = null
      }

      this.lastScrollY = currentScrollY
    },
    updateOptionsSelected(value) {
      this.optionsSelected = value
    },
  },
}
